import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  LocalFireDepartmentOutlined,
  WhatshotOutlined,
  PsychologyOutlined,
  ShieldOutlined,
  ForestOutlined,
  WaterOutlined,
  BoltOutlined,
  TrendingUpOutlined,
  SearchOutlined,
  RefreshOutlined,
  PictureAsPdfOutlined,
  CloseOutlined,
} from '@mui/icons-material';

import StatCard from '../../atoms/StatCard/StatCard';
import ExportMenu from '../../atoms/ExportMenu/ExportMenu';
import DateRangePicker from '../../molecules/DateRangePicker/DateRangePicker';
import ViewSwitcher from '../../atoms/ViewSwitcher/ViewSwitcher';
import AdminHierarchyFilter from '../../atoms/AdminHierarchyFilter/AdminHierarchyFilter';
import ActivityInsightsChart from '../../molecules/charts/ActivityInsightsChart/ActivityInsightsChart';
import DonutChart from '../../molecules/charts/DonutChart/DonutChart';
import DrillDownPanel from '../../molecules/charts/DrillDownPanel/DrillDownPanel';
import {
  buildExportName,
  buildFilterLabels,
  exportEchartsPNG,
  exportNodeAsPNG,
  exportRowsAsCSV,
  formatNumberFR,
  generateSecuFireReport,
} from '../../../utils/exportChartsUtils';

import './SecufireActionLogs.css';

export const MS_CONFIGS = {
  'historic-fire_service': {
    label: 'Historique des feux',
    Icon: LocalFireDepartmentOutlined,
    color: '#be1622',
    bg: 'rgba(190,22,34,0.07)',
  },
  'active-fire_service': {
    label: 'Points chauds',
    Icon: WhatshotOutlined,
    color: '#EAB308',
    bg: 'rgba(234,179,8,0.09)',
  },
  physical_model: {
    label: 'Modèle physique',
    Icon: PsychologyOutlined,
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.07)',
  },
  dfci_service: {
    label: 'Analyse terrain',
    Icon: ShieldOutlined,
    color: '#F97316',
    bg: 'rgba(249,115,22,0.07)',
  },
  vegetation_service: {
    label: 'Végétation',
    Icon: ForestOutlined,
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.07)',
  },
  weather_service: {
    label: 'Météo',
    Icon: WaterOutlined,
    color: '#0284C7',
    bg: 'rgba(2,132,199,0.07)',
  },
};

const MS_ORDER = [
  'historic-fire_service',
  'active-fire_service',
  'physical_model',
  'dfci_service',
  'vegetation_service',
  'weather_service',
];

const VIEW_MODES = [
  { key: 'daily', label: 'Jour' },
  { key: 'weekly', label: 'Semaine' },
  { key: 'monthly', label: 'Mois' },
];

const DRILL_MODES = [
  { key: 'endpoint', label: 'Par endpoint' },
  { key: 'user', label: 'Par utilisateur' },
];

const SecufireActionLogs = ({
  data,
  loading,
  startDate,
  endDate,
  onDateChange,

  users = [],

  selectedMs,
  onMsSelect,

  onEndpointDrill,
  endpointDrillData,
  onEndpointDrillClose,

  onUserDrill,
  userDrillData,
  onUserDrillClose,

  admins = [],
  selectedAdminId,
  onAdminSelect,

  onRefresh,
  refreshing = false,

  notifSlot,

  isInternal,
  onIsInternalChange,
}) => {
  const [viewMode, setViewMode] = useState('daily');
  const [drillMode, setDrillMode] = useState('endpoint');
  const [drillSearch, setDrillSearch] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);

  const activityChartRef = useRef(null);
  const donutChartRef = useRef(null);
  const drillDownRef = useRef(null);

  const previousDataRef = useRef(data);

  useEffect(() => {
    const previousData = previousDataRef.current;
    if (previousData !== data) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }
    previousDataRef.current = data;
  }, [data, onEndpointDrillClose, onUserDrillClose]);

  const previousPeriodRef = useRef({ startDate, endDate });

  useEffect(() => {
    const previous = previousPeriodRef.current;
    if (previous.startDate !== startDate || previous.endDate !== endDate) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }
    previousPeriodRef.current = { startDate, endDate };
  }, [startDate, endDate, onEndpointDrillClose, onUserDrillClose]);

  const previousSelectedMsRef = useRef(null);

  useEffect(() => {
    const current = JSON.stringify(
      selectedMs instanceof Set
        ? [...selectedMs].sort()
        : Array.isArray(selectedMs)
          ? [...selectedMs].sort()
          : selectedMs
    );
    if (previousSelectedMsRef.current !== null && previousSelectedMsRef.current !== current) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }
    previousSelectedMsRef.current = current;
  }, [selectedMs, onEndpointDrillClose, onUserDrillClose]);

  const handleRefresh = () => {
    onEndpointDrillClose?.();
    onUserDrillClose?.();
    onRefresh?.();
  };

  const selectedMsSet = useMemo(() => {
    if (!selectedMs) return new Set(MS_ORDER);
    if (selectedMs instanceof Set) return selectedMs.size > 0 ? selectedMs : new Set(MS_ORDER);
    if (Array.isArray(selectedMs)) return new Set(selectedMs.length > 0 ? selectedMs : MS_ORDER);
    return new Set([selectedMs]);
  }, [selectedMs]);

  const handleMsToggle = (msKey) => {
    const next = new Set(selectedMsSet);
    if (next.has(msKey)) {
      if (next.size > 1) next.delete(msKey);
    } else {
      next.add(msKey);
    }

    const currentEndpointMs = endpointDrillData?.microservice;
    const currentUserDrill = userDrillData;

    if (currentEndpointMs && !next.has(currentEndpointMs)) onEndpointDrillClose?.();
    if (currentUserDrill) onUserDrillClose?.();

    onMsSelect(next);
  };

  const { totalCalls, activeMsCount, topMs } = useMemo(() => {
    const list = data?.microservices || [];
    const totalCalls = list.reduce((sum, m) => sum + (m.total_usage || 0), 0);
    const activeMsCount = selectedMsSet.size;
    const selectedList = list.filter((m) => selectedMsSet.has(m.microservice));
    const topMs = selectedList.reduce(
      (best, m) => (!best || m.total_usage > best.total_usage ? m : best),
      null
    );
    return { totalCalls, activeMsCount, topMs };
  }, [data, selectedMsSet]);

  const adaptedMicroservices = useMemo(
    () =>
      (data?.microservices || [])
        .filter((m) => selectedMsSet.has(m.microservice))
        .map((m) => ({ ...m, color: MS_CONFIGS[m.microservice]?.color })),
    [data, selectedMsSet]
  );

  const mergedDailyTrend = useMemo(() => {
    const map = {};
    (data?.microservices || [])
      .filter((m) => selectedMsSet.has(m.microservice))
      .forEach((m) => {
        (m.daily_trend || []).forEach((d) => {
          const key = (d.date || d.day || '').slice(0, 10);
          if (!key) return;
          if (!map[key]) map[key] = { date: key, total_activities: 0 };
          map[key].total_activities += d.total_activities ?? d.count ?? 0;
        });
      });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, selectedMsSet]);

  const selectedAdminName = admins.find((a) => a.id === selectedAdminId)?.username || null;

  // Filtres actifs à répercuter dans le nommage de TOUS les exports
  // (mêmes conventions que la page "Logs d'authentification").
  const activeFilters = useMemo(() => [
    selectedAdminName ? `admin-${selectedAdminName}` : null,
    isInternal === true ? 'comptes-internes' : isInternal === false ? 'comptes-externes' : null,
  ], [selectedAdminName, isInternal]);

  // Libellés des filtres actifs : utilisés dans l'en-tête du PDF ET dans le
  // bandeau des captures PNG.
  const baseFilterLabels = useMemo(
    () => buildFilterLabels({ adminName: selectedAdminName, isInternal, startDate, endDate }),
    [selectedAdminName, isInternal, startDate, endDate],
  );

  const pngFilterLabels = useMemo(() => [
    ...baseFilterLabels,
    `Services : ${[...selectedMsSet].map((k) => MS_CONFIGS[k]?.label || k).join(' + ')}`,
  ], [baseFilterLabels, selectedMsSet]);

  // En-tête des PNG : titre de la section + page + filtres actifs
  const pngMeta = (title) => ({
    title,
    subtitle: 'SecuFire Action · Logs d’utilisation',
    filters: pngFilterLabels,
  });

  // ── Handlers d'export ────────────────────────────────────────────

  const handleExportTrendPNG = () => exportEchartsPNG(
    activityChartRef,
    buildExportName({ section: 'Evolution temporelle', startDate, endDate, filters: activeFilters }),
    pngMeta('Évolution temporelle'),
  );
  const handleExportTrendCSV = () => exportRowsAsCSV(
    mergedDailyTrend.map((d) => ({ date: d.date, total_activites: d.total_activities })),
    buildExportName({ section: 'Evolution temporelle', startDate, endDate, filters: activeFilters }),
  );

  const handleExportDonutPNG = () => exportNodeAsPNG(
    donutChartRef.current?.getNode?.(),
    buildExportName({ section: 'Repartition des microservices', startDate, endDate, filters: activeFilters }),
    pngMeta('Répartition des microservices'),
  );
  const handleExportDonutCSV = () => exportRowsAsCSV(
    (data?.microservices || [])
      .filter((m) => selectedMsSet.has(m.microservice))
      .map((m) => ({
        microservice: MS_CONFIGS[m.microservice]?.label || m.microservice,
        appels: m.total_usage || 0,
      })),
    buildExportName({ section: 'Repartition des microservices', startDate, endDate, filters: activeFilters }),
  );

  const handleExportDrillPNG = () => exportEchartsPNG(
    drillDownRef,
    buildExportName({
      section: drillMode === 'endpoint' ? 'Detail par endpoint' : 'Detail par utilisateur',
      startDate,
      endDate,
      filters: activeFilters,
    }),
    pngMeta(drillMode === 'endpoint' ? 'Détail par service — par endpoint' : 'Détail par service — par utilisateur'),
  );
  const handleExportDrillCSV = () => exportRowsAsCSV(
    drillDownRef.current?.getCSVRows?.(),
    buildExportName({
      section: drillMode === 'endpoint' ? 'Detail par endpoint' : 'Detail par utilisateur',
      startDate,
      endDate,
      filters: activeFilters,
    }),
  );

  // Export PDF global : un vrai rapport de traçabilité SecuFire Action
  // (synthèse + tableaux complets), pas une capture d'écran de la page.
  const handleExportPDF = async () => {
    if (exportingPDF) return;
    setExportingPDF(true);
    try {
      const selectedMsLabels = [...selectedMsSet].map((k) => MS_CONFIGS[k]?.label).filter(Boolean);

      const drillRows = drillDownRef.current?.getCSVRows?.() || [];
      const drillColumns = drillRows.length
        ? Object.keys(drillRows[0]).map((k) => ({ key: k, label: k, width: 1 }))
        : [];

      const extraSections = [
        {
          title: `Détail par service — vue ${drillMode === 'endpoint' ? 'endpoints' : 'utilisateurs'}`,
          table: drillRows.length ? { columns: drillColumns, rows: drillRows } : undefined,
          paragraph: drillRows.length ? undefined : 'Aucune donnée disponible pour la vue sélectionnée.',
        },
      ];

      // Historique d'un endpoint : uniquement si le switcher est sur "Par endpoint".
      if (endpointDrillData && drillMode === 'endpoint') {
        const calls = endpointDrillData.calls || [];
        extraSections.push({
          title: `Historique — endpoint sélectionné (${endpointDrillData.msLabel || ''})`,
          table: calls.length ? {
            columns: [
              { key: 'date', label: 'Date', width: 1 },
              { key: 'utilisateur', label: 'Utilisateur', width: 1 },
              { key: 'microservice', label: 'Microservice', width: 1 },
              { key: 'details', label: 'Détails', width: 4, maxLines: 4 },
            ],
            rows: calls.map((c) => ({
              date: new Date(c.timestamp).toLocaleString('fr-FR'),
              utilisateur: c.username || '—',
              microservice: endpointDrillData.msLabel || '—',
              details: c.details || '',
            })),
          } : undefined,
          paragraph: calls.length ? undefined : 'Aucun appel trouvé pour cette période.',
        });
      }

      // Historique d'un utilisateur : uniquement si le switcher est sur "Par utilisateur".
      // La colonne « Détails » est volontairement absente dans ce mode.
      if (userDrillData && drillMode === 'user') {
        const calls = userDrillData.calls || [];
        extraSections.push({
          title: `Historique — utilisateur sélectionné (${userDrillData.username || ''})`,
          table: calls.length ? {
            columns: [
              { key: 'date', label: 'Date', width: 1 },
              { key: 'endpoint', label: 'Endpoint', width: 2 },
              { key: 'microservice', label: 'Microservice', width: 1 },
            ],
            rows: calls.map((c) => ({
              date: new Date(c.timestamp).toLocaleString('fr-FR'),
              endpoint: c.endpoint || '—',
              microservice: c.msLabel || '—',
            })),
          } : undefined,
          paragraph: calls.length ? undefined : 'Aucun appel trouvé pour cette période.',
        });
      }

      await generateSecuFireReport({
        fileName: buildExportName({
          section: 'rapport logs secufire action',
          startDate,
          endDate,
          filters: activeFilters,
        }),
        title: 'Rapport de traçabilité — SecuFire Action',
        subtitle: selectedMsLabels.join(' + '),
        filtersLine: baseFilterLabels.join('   ·   '),
        kpis: [
          { label: 'Total appels', value: formatNumberFR(totalCalls) },
          { label: 'Services actifs', value: `${formatNumberFR(activeMsCount)} / ${MS_ORDER.length}` },
          { label: 'Service dominant', value: topMs ? MS_CONFIGS[topMs.microservice]?.label || topMs.microservice : 'N/A' },
        ],
        dailyTrend: mergedDailyTrend,
        endpointDistribution: (data?.microservices || [])
          .filter((m) => selectedMsSet.has(m.microservice))
          .map((m) => ({
            endpoint: MS_CONFIGS[m.microservice]?.label || m.microservice,
            total_usage: m.total_usage || 0,
          })),
        endpointDistributionTitle: 'Répartition des microservices',
        endpointDistributionColumnLabel: 'Microservice',
        extraSections,
      });
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="sal-loading">
        <div className="sal-spinner" />
        <p>Chargement des logs SecuFire Action…</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="sal-wrapper">
      {/* HEADER */}
      <div className="sal-header">
        <div className="sal-stats-block">
          <div className="sal-kpis-row">
            <div className="sal-kpi-item">
              <StatCard
                icon={<BoltOutlined />}
                label="Total Appels"
                value={totalCalls.toLocaleString()}
                variant="red"
                small
              />
            </div>
            <div className="sal-kpi-item sal-kpi-item--full">
              <StatCard
                icon={<TrendingUpOutlined />}
                label="Service dominant"
                value={topMs ? MS_CONFIGS[topMs.microservice]?.label || topMs.microservice : 'N/A'}
                variant="orange"
                small
              />
            </div>
          </div>
        </div>

        <div className="header-sep" />

        <div className="sal-filters-block">
          <div className="sal-filter-group">
            <span className="sal-filter-label">Admin :</span>
            <AdminHierarchyFilter
              admins={admins}
              selectedAdminId={selectedAdminId}
              onSelect={(value) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onAdminSelect?.(value);
              }}
            />
          </div>

          <div className="sal-filter-group">
            <span className="sal-filter-label">Comptes :</span>
            <select
              value={isInternal === null ? 'all' : isInternal ? 'internal' : 'external'}
              onChange={(e) => {
                const v = e.target.value;
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onIsInternalChange(v === 'all' ? null : v === 'internal' ? true : false);
              }}
              className="sal-internal-select"
            >
              <option value="all">Tous</option>
              <option value="external">Externes</option>
              <option value="internal">Internes</option>
            </select>
          </div>

          <div className="sal-filter-group">
            <span className="sal-filter-label">Période :</span>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartChange={(date) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onDateChange(date, endDate);
              }}
              onEndChange={(date) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onDateChange(startDate, date);
              }}
            />
          </div>
        </div>

        <div className="header-sep" />

        <div className="sal-actions-slot">
          <button
            className={`sal-pdf-btn${exportingPDF ? ' sal-pdf-btn--loading' : ''}`}
            onClick={handleExportPDF}
            disabled={exportingPDF}
            title="Exporter le rapport de traçabilité en PDF"
          >
            {exportingPDF
              ? <span className="sal-pdf-spinner" />
              : <PictureAsPdfOutlined style={{ fontSize: 14 }} />}
            <span>{exportingPDF ? 'Génération…' : 'PDF'}</span>
          </button>
          <button
            className={`sal-refresh-btn${refreshing ? ' sal-refresh-btn--loading' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Rafraîchir"
          >
            <RefreshOutlined style={{ fontSize: 15 }} />
          </button>
          {notifSlot}
        </div>
      </div>

      {/* ADMIN BADGE */}
      {selectedAdminId && selectedAdminName && (
        <div className="sal-admin-badge">
          <span className="sal-admin-badge-dot" />
          <span className="sal-admin-badge-text">
            Données filtrées · hiérarchie de <strong>{selectedAdminName}</strong>
          </span>
        </div>
      )}

      {/* INTERNAL BADGE */}
      {isInternal !== null && (
        <div
          className="sal-admin-badge"
          style={{
            background: isInternal ? '#f0fdf4' : '#eff6ff',
            borderColor: isInternal ? '#86efac' : '#93c5fd',
          }}
        >
          <span className="sal-admin-badge-dot" style={{ background: isInternal ? '#16a34a' : '#2563eb' }} />
          <span className="sal-admin-badge-text">
            Filtre actif · comptes{' '}
            <strong style={{ color: isInternal ? '#16a34a' : '#2563eb' }}>
              {isInternal ? 'internes' : 'externes'}
            </strong>{' '}
            uniquement
          </span>
        </div>
      )}

      {/* MICRO-SERVICES */}
      <div className="sal-ms-switcher">
        {MS_ORDER.map((msKey) => {
          const cfg = MS_CONFIGS[msKey];
          const msD = data.microservices?.find((m) => m.microservice === msKey);
          const calls = msD?.total_usage || 0;
          const isActive = selectedMsSet.has(msKey);

          return (
            <button
              key={msKey}
              className={`sal-ms-tab${isActive ? ' sal-ms-tab--active' : ''}`}
              style={{ '--ms-color': cfg.color, '--ms-bg': cfg.bg }}
              onClick={() => handleMsToggle(msKey)}
            >
              <cfg.Icon sx={{ fontSize: 16 }} />
              <span className="sal-ms-tab-label">{cfg.label}</span>
              <span className="sal-ms-tab-count">{calls.toLocaleString()}</span>
              {isActive && <span className="sal-ms-tab-check">✓</span>}
            </button>
          );
        })}
      </div>

      {/* TREND */}
      <div className="sal-section-label">Tendance d'utilisation</div>

      <div className="sal-top-row">
        <div className="sal-chart-card sal-top-card">
          <div className="sal-chart-header--row">
            <div className="sal-chart-header-left">
              <h3 className="sal-chart-title">Évolution temporelle</h3>
              <span className="sal-chart-subtitle">
                {[...selectedMsSet].map((k, i) => (
                  <React.Fragment key={k}>
                    {i > 0 && <span style={{ color: '#94a3b8' }}> + </span>}
                    <span style={{ color: MS_CONFIGS[k]?.color, fontWeight: 700 }}>
                      {MS_CONFIGS[k]?.label}
                    </span>
                  </React.Fragment>
                ))}
              </span>
              <div className="sal-chart-switcher-row">
                <ViewSwitcher
                  modes={VIEW_MODES}
                  value={viewMode}
                  onChange={setViewMode}
                  color="#be1622"
                />
              </div>
            </div>

            <ExportMenu
              color="#be1622"
              onExportPNG={handleExportTrendPNG}
              onExportCSV={handleExportTrendCSV}
            />
          </div>

          <div className="sal-chart-body">
            <ActivityInsightsChart
              ref={activityChartRef}
              dailyTrend={mergedDailyTrend}
              microservices={adaptedMicroservices}
              startDate={startDate}
              endDate={endDate}
              viewMode={viewMode}
            />
          </div>
        </div>

        <div className="sal-chart-card sal-top-card">
          <div className="sal-chart-header">
            <div className="sal-chart-header-left">
              <h3 className="sal-chart-title">Répartition des microservices</h3>
              <span className="sal-chart-subtitle">Part de chaque microservice</span>
            </div>
            <ExportMenu
              color="#be1622"
              onExportPNG={handleExportDonutPNG}
              onExportCSV={handleExportDonutCSV}
            />
          </div>

          <div className="sal-chart-body sal-donut-body">
            <DonutChart
              ref={donutChartRef}
              microservices={data.microservices || []}
              selectedMsSet={selectedMsSet}
              totalCalls={totalCalls}
            />
          </div>
        </div>
      </div>

      {/* DRILL DOWN
          Un seul composant (DrillDownPanel) qui s'affiche en DEUX cartes
          séparées, comme "Classement des utilisateurs" / "Profil d'activité"
          dans les logs utilisateurs. L'en-tête (titre, switcher, export,
          recherche) est passé en slot : il est rendu dans la carte de gauche. */}
      <div className="sal-section-label">Détail par service</div>

      <div className="sal-drill-wrapper">
        <DrillDownPanel
          ref={drillDownRef}
          mode={drillMode}
          searchQuery={drillSearch}
          selectedMsSet={selectedMsSet}
          msData={data.microservices || []}
          users={users}
          onEndpointDrill={onEndpointDrill}
          onUserDrill={onUserDrill}
          endpointDrillData={endpointDrillData}
          userDrillData={userDrillData}
          onEndpointDrillClose={onEndpointDrillClose}
          onUserDrillClose={onUserDrillClose}
          startDate={startDate}
          endDate={endDate}
          activeFilters={activeFilters}
          headerSlot={(
            <div className="sal-drill-header sal-chart-header--search">
              <div className="sal-chart-header-left">
                <h3 className="sal-chart-title">Détail par service</h3>
                <span className="sal-chart-subtitle sal-drill-hint">
                  {drillMode === 'endpoint'
                    ? `${[...selectedMsSet].map((k) => MS_CONFIGS[k]?.label).join(' + ')} · cliquer sur un endpoint`
                    : 'Cliquer sur un utilisateur pour voir son historique'}
                </span>
                <div className="sal-chart-switcher-row">
                  <ViewSwitcher
                    modes={DRILL_MODES}
                    value={drillMode}
                    onChange={(m) => {
                      onEndpointDrillClose?.();
                      onUserDrillClose?.();
                      setDrillMode(m);
                      setDrillSearch('');
                    }}
                    color="#be1622"
                  />
                </div>
              </div>

              <div className="sal-header-actions">
                <ExportMenu
                  color="#be1622"
                  onExportPNG={handleExportDrillPNG}
                  onExportCSV={handleExportDrillCSV}
                />
                <div className={`sal-drill-search${drillSearch ? ' sal-drill-search--active' : ''}`}>
                  <SearchOutlined className="sal-drill-search__icon" />
                  <input
                    type="text"
                    className="sal-drill-search__input"
                    placeholder={drillMode === 'endpoint' ? 'Nom d’endpoint…' : 'Nom d’utilisateur…'}
                    value={drillSearch}
                    onChange={(e) => setDrillSearch(e.target.value)}
                  />
                  {drillSearch && (
                    <button
                      className="sal-drill-search__clear"
                      onClick={() => setDrillSearch('')}
                      title="Effacer"
                    >
                      <CloseOutlined style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default SecufireActionLogs;